import type { ColDef, ValueGetterParams } from 'ag-grid-community'
import type { Match } from '../../../../../../api/useSupplierReconciliation/types/match'

export interface DualColumnFilterConfig {
  field: string
  numberFieldTitle?: string
  statusFieldTitle?: string
  valueGetter: (params: ValueGetterParams<Match>) => string
  statusValueGetter: (params: ValueGetterParams<Match>) => string | string[]
}

export function createDualColumnFilter(config: DualColumnFilterConfig): ColDef<Match>['filter'] {
  return 'agMultiColumnFilter'
}

export function createDualColumnFilterParams(config: DualColumnFilterConfig): ColDef<Match>['filterParams'] {
  return {
    filters: [
      {
        filter: 'agSetColumnFilter',
        display: 'subMenu',
        title: config.numberFieldTitle || 'Filter By Number',
        filterParams: {
          field: config.field,
          buttons: ['reset'],
        },
      },
      {
        filter: 'agSetColumnFilter',
        display: 'subMenu',
        title: config.statusFieldTitle || 'Filter By Status',
        filterParams: {
          field: `${config.field}_status`,
          valueGetter: config.statusValueGetter,
          buttons: ['reset'],
        },
      },
    ],
  }
}

export function createInvoiceStatusValueGetter() {
  return (params: ValueGetterParams<Match>) => {
    if (!params.data?.invoice_numbers || !Array.isArray(params.data.invoice_numbers)) return 'Missing'
    return params.data.invoice_numbers.length > 0 ? 'Exists' : 'Missing'
  }
}

export function createBolStatusValueGetter() {
  return (params: ValueGetterParams<Match>) => {
    if (!params.data) return ['Missing']

    const statuses = []
    const bolNumbers = params.data?.bol_numbers || []

    if (!params.data?.bol_numbers || !Array.isArray(params.data.bol_numbers) || bolNumbers.length === 0) {
      statuses.push('Missing')
    } else {
      // Check for movement validation errors
      const hasMovementValidationError = params.data.warnings?.some((w: any) => w.type === 'movement_validation')
      if (hasMovementValidationError) {
        statuses.push('Order Movement Validations')
      }

      // Check for delivery reconciliation errors
      const hasDeliveryReconciliationError = params.data.warnings?.some(
        (w: any) => w.type === 'delivery_reconciliation'
      )
      if (hasDeliveryReconciliationError) {
        statuses.push('Delivery Reconciliation Errors')
      }

      // Check if any BOLs have null IDs
      const hasNullBolIds = bolNumbers.some((bol: { id: string; number: string }) => !bol.id)
      if (hasNullBolIds) {
        statuses.push('Expected')
      }

      // Check if any BOLs have valid IDs
      const hasValidBols = bolNumbers.some((bol: { id: string; number: string }) => bol.id)
      if (hasValidBols) {
        statuses.push('Exists')
      }
    }

    return statuses
  }
}

export function createOrderStatusValueGetter() {
  return (params: ValueGetterParams<Match>) => {
    if (!params.data?.order_numbers || !Array.isArray(params.data.order_numbers)) return 'Missing'
    return params.data.order_numbers.length > 0 ? 'Attached' : 'Missing'
  }
}

export function createPoStatusValueGetter() {
  return (params: ValueGetterParams<Match>) => {
    if (!params.data?.po_numbers || !Array.isArray(params.data.po_numbers)) return 'Missing'
    return params.data.po_numbers.length > 0 ? 'Attached' : 'Missing'
  }
}

export function createBolDateStatusValueGetter() {
  return (params: ValueGetterParams<Match>) => {
    if (!params.data?.bol_date) return 'Missing'
    return 'Attached'
  }
}

export interface DateStatusDualColumnFilterConfig {
  field: string
  dateFieldTitle?: string
  statusFieldTitle?: string
  statusValueGetter: (params: ValueGetterParams<Match>) => string | string[]
}

export function createDateStatusDualColumnFilter(config: DateStatusDualColumnFilterConfig): ColDef<Match>['filter'] {
  return 'agMultiColumnFilter'
}

export function createDateStatusDualColumnFilterParams(
  config: DateStatusDualColumnFilterConfig
): ColDef<Match>['filterParams'] {
  return {
    filters: [
      {
        filter: 'agDateColumnFilter',
        display: 'subMenu',
        title: config.dateFieldTitle || 'Filter By Date',
        filterParams: {
          field: config.field,
          buttons: ['reset'],
        },
      },
      {
        filter: 'agSetColumnFilter',
        display: 'subMenu',
        title: config.statusFieldTitle || 'Filter By Status',
        filterParams: {
          field: `${config.field}_status`,
          valueGetter: config.statusValueGetter,
          buttons: ['reset'],
        },
      },
    ],
  }
}
